import React, { useState } from 'react';
import { Check, X, Edit3, AlertTriangle, CheckCircle, Sparkles } from 'lucide-react';

const ComparisonCard = ({ comparison, onConfirm, profileImage }) => {
  const [selectedValues, setSelectedValues] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Field labels in Spanish
  const fieldLabels = {
    playerName: 'Nombre',
    playerLevel: 'Nivel',
    countryFlag: 'Bandera',
    gender: 'Género',
    power: 'Poder',
    troopPower: 'Muertes',
    commanderLevel: 'LV Profesión',
    vipLevel: 'VIP',
    allianceName: 'Alianza',
    allianceRank: 'Rango',
    castleRank: 'Servidor'
  };

  // Initialize selected values with auto-matched fields
  React.useEffect(() => {
    const initial = {};
    Object.entries(comparison.fields).forEach(([field, data]) => {
      if (data.matches) {
        initial[field] = data.gpt; // Both are the same, use GPT value
      }
    });
    setSelectedValues(initial);
  }, [comparison]);

  const selectValue = (field, source) => {
    const value = source === 'gpt' ? comparison.fields[field].gpt : comparison.fields[field].claude;
    setSelectedValues(prev => ({ ...prev, [field]: value }));
  };

  const startEdit = (field) => {
    setEditingField(field);
    setEditValue(selectedValues[field] || comparison.fields[field].gpt || '');
  };

  const saveEdit = (field) => {
    setSelectedValues(prev => ({ ...prev, [field]: editValue }));
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const allFieldsSelected = () => {
    return Object.keys(comparison.fields).every(field => 
      selectedValues[field] !== undefined && selectedValues[field] !== null
    );
  };

  const handleConfirm = () => {
    const finalProfile = {
      ...selectedValues,
      profileImage: profileImage
    };
    onConfirm(finalProfile);
  };

  const matchCount = Object.values(comparison.fields).filter(f => f.matches).length;
  const totalFields = Object.keys(comparison.fields).length;
  const matchPercentage = Math.round((matchCount / totalFields) * 100);

  return (
    <div className="space-y-6">
      {/* Header Status */}
      <div className={`game-card p-6 ${comparison.matches ? 'border-green-500' : 'border-yellow-500'}`}>
        <div className="flex items-center gap-4">
          {comparison.matches ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center border-2 border-green-300 shadow-lg shadow-green-500/30">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-green-400">¡Verificación Exitosa!</h3>
                <p className="text-cyan-300/80">Ambas IAs coinciden en todos los campos ({matchPercentage}%)</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center border-2 border-yellow-300 shadow-lg shadow-yellow-500/30 animate-pulse">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-yellow-400">Diferencias Encontradas</h3>
                <p className="text-cyan-300/80">
                  Las IAs coinciden en {matchCount} de {totalFields} campos ({matchPercentage}%). 
                  Selecciona la versión correcta para cada diferencia.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="game-card overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-4 gap-4 p-4 bg-gradient-to-r from-[#0a1f3a] to-[#0d2847] border-b border-cyan-500/30">
          <div className="text-cyan-400 font-bold text-sm">CAMPO</div>
          <div className="text-center">
            <span className="px-3 py-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg text-white text-xs font-bold">
              GPT-4o
            </span>
          </div>
          <div className="text-center">
            <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg text-white text-xs font-bold">
              Claude
            </span>
          </div>
          <div className="text-cyan-400 font-bold text-sm text-center">ESTADO</div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-cyan-500/20">
          {Object.entries(comparison.fields).map(([field, data]) => (
            <div 
              key={field}
              className={`grid grid-cols-4 gap-4 p-4 items-center transition-colors ${
                data.matches ? 'bg-green-500/5' : 'bg-yellow-500/10'
              }`}
            >
              {/* Field Name */}
              <div className="text-white font-semibold text-sm">
                {fieldLabels[field] || field}
              </div>

              {/* GPT Value */}
              <div className="text-center">
                {editingField === field ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 px-2 py-1 bg-[#0a1628] border border-cyan-500/50 rounded text-white text-sm focus:outline-none focus:border-cyan-400"
                      autoFocus
                    />
                    <button 
                      onClick={() => saveEdit(field)}
                      className="p-1 bg-green-500 rounded hover:bg-green-400"
                    >
                      <Check className="w-4 h-4 text-white" />
                    </button>
                    <button 
                      onClick={cancelEdit}
                      className="p-1 bg-red-500 rounded hover:bg-red-400"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => !data.matches && selectValue(field, 'gpt')}
                    disabled={data.matches}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all w-full ${
                      data.matches 
                        ? 'bg-green-500/20 text-green-300 cursor-default'
                        : selectedValues[field] === data.gpt
                          ? 'bg-green-500 text-white ring-2 ring-green-300'
                          : 'bg-[#0a1f3a] text-cyan-200 hover:bg-green-500/30 cursor-pointer'
                    }`}
                  >
                    {data.gpt ?? 'N/A'}
                  </button>
                )}
              </div>

              {/* Claude Value */}
              <div className="text-center">
                {!editingField || editingField !== field ? (
                  <button
                    onClick={() => !data.matches && selectValue(field, 'claude')}
                    disabled={data.matches}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all w-full ${
                      data.matches 
                        ? 'bg-green-500/20 text-green-300 cursor-default'
                        : selectedValues[field] === data.claude
                          ? 'bg-purple-500 text-white ring-2 ring-purple-300'
                          : 'bg-[#0a1f3a] text-cyan-200 hover:bg-purple-500/30 cursor-pointer'
                    }`}
                  >
                    {data.claude ?? 'N/A'}
                  </button>
                ) : (
                  <div className="px-3 py-2 rounded-lg text-sm text-cyan-500/50">
                    Editando...
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center justify-center gap-2">
                {data.matches ? (
                  <span className="flex items-center gap-1 text-green-400 text-sm font-medium">
                    <Check className="w-4 h-4" />
                    Auto
                  </span>
                ) : selectedValues[field] !== undefined ? (
                  <span className="flex items-center gap-1 text-cyan-400 text-sm font-medium">
                    <Check className="w-4 h-4" />
                    OK
                  </span>
                ) : (
                  <span className="text-yellow-400 text-sm font-medium">Elige</span>
                )}
                {!data.matches && (
                  <button
                    onClick={() => startEdit(field)}
                    className="p-1.5 bg-cyan-500/20 hover:bg-cyan-500/40 rounded-lg transition-colors"
                    title="Editar manualmente"
                  >
                    <Edit3 className="w-4 h-4 text-cyan-400" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-cyan-300/70">
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          Coinciden (auto-seleccionado)
        </span>
        <span className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-yellow-500"></div>
          Diferencias (elige uno)
        </span>
      </div>

      {/* Confirm Button */}
      <div className="text-center">
        <button
          onClick={handleConfirm}
          disabled={!allFieldsSelected()}
          data-testid="confirm-selection-btn"
          className={`game-btn game-btn-green text-xl px-12 py-4 flex items-center gap-3 mx-auto ${
            !allFieldsSelected() ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <Sparkles className="w-6 h-6" />
          {allFieldsSelected() ? 'CONFIRMAR SELECCIÓN' : 'SELECCIONA TODOS LOS CAMPOS'}
        </button>
      </div>
    </div>
  );
};

export default ComparisonCard;
